import React, { useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Paper,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import {
  Person as PersonIcon,
  ShoppingBag as BagIcon,
  Favorite as WishlistIcon,
  ShoppingCart as OrdersIcon,
  SupportAgent as SupportAgentIcon,
  ArrowForwardIosRounded as ArrowForwardIosRoundedIcon,
  NotificationsNoneRounded as NotificationsNoneRoundedIcon,
  EmailOutlined as EmailOutlinedIcon,
  SmsOutlined as SmsOutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  CalendarMonthOutlined as CalendarMonthOutlinedIcon,
  CheckRounded as CheckRoundedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
};

const SidebarItem = ({ icon, label, active, onClick }) => (
  <ListItem
    component="button"
    onClick={onClick}
    sx={{
      width: "100%",
      border: 0,
      outline: "none",
      textAlign: "left",
      background: active ? COLORS.black : "transparent",
      color: active ? COLORS.white : COLORS.mutedBlack,
      borderRadius: 0,
      px: 2,
      py: 1.6,
      mb: 0.6,
      cursor: "pointer",
      transition: "all 0.25s ease",
      "&:hover": {
        background: active ? COLORS.black : COLORS.cream,
        color: active ? COLORS.white : COLORS.black,
        transform: "translateX(3px)",
      },
      "& .MuiListItemIcon-root": {
        minWidth: 40,
        color: active ? COLORS.goldLight : COLORS.grey,
        transition: "color 0.25s ease",
      },
    }}
  >
    <ListItemIcon>{icon}</ListItemIcon>
    <ListItemText
      primary={label}
      primaryTypographyProps={{
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        letterSpacing: "0.02em",
      }}
    />
    <ArrowForwardIosRoundedIcon
      sx={{
        fontSize: 12,
        opacity: active ? 0.9 : 0,
        transform: active ? "translateX(0)" : "translateX(-5px)",
        transition: "all 0.25s ease",
      }}
    />
  </ListItem>
);

const ToggleSwitch = ({ checked, onChange }) => (
  <Box
    component="button"
    type="button"
    onClick={onChange}
    aria-pressed={checked}
    sx={{
      width: 58,
      height: 30,
      border: 0,
      borderRadius: 0,
      p: 0,
      position: "relative",
      backgroundColor: checked ? COLORS.black : "#E4E0D9",
      cursor: "pointer",
      transition: "background-color 0.25s ease",
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        position: "absolute",
        width: 22,
        height: 22,
        top: 4,
        left: checked ? 32 : 4,
        backgroundColor: checked ? COLORS.goldLight : COLORS.white,
        transition: "left 0.25s ease, background-color 0.25s ease",
      }}
    />
  </Box>
);

const SettingRow = ({
  icon,
  title,
  description,
  checked,
  onChange,
  last = false,
}) => (
  <>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
        py: 2.5,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            border: `1px solid ${COLORS.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            backgroundColor: COLORS.softCream,
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              fontSize: 20,
              color: COLORS.gold,
            },
          })}
        </Box>

        <Box sx={{ textAlign: "left" }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: COLORS.black,
              mb: 0.4,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              color: COLORS.grey,
              lineHeight: 1.6,
            }}
          >
            {description}
          </Typography>
        </Box>
      </Stack>

      <ToggleSwitch checked={checked} onChange={onChange} />
    </Box>

    {!last && <Divider sx={{ borderColor: COLORS.border }} />}
  </>
);

export default function Setting() {
  const navigate = useNavigate();

  const [settings, setSettings] = useState({
    email: true,
    sms: false,
    push: true,
    weekly: false,
  });

  const toggleSetting = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const openAppRedirect = () => {
    const userId = sessionStorage.getItem("userId");
    const appPackage = "com.ytctechventures.hindustanwardrobe";
    const intentUrl = `intent://ownerProfile?ownerId=${userId}#Intent;scheme=https;package=${appPackage};end`;

    window.location.href = intentUrl;

    setTimeout(() => {
      window.location.href = `https://play.google.com/store/apps/details?id=${appPackage}`;
    }, 800);
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: COLORS.cream,
          color: COLORS.black,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: 1440,
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 4, md: 7 },
          }}
        >
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: COLORS.gold,
                mb: 1.2,
              }}
            >
              Account Settings
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: { xs: 34, sm: 42, md: 52 },
                lineHeight: 1.05,
                fontWeight: 400,
                color: COLORS.black,
                mb: 1.5,
              }}
            >
              Your preferences,
              <br />
              your experience.
            </Typography>

            <Typography
              sx={{
                maxWidth: 600,
                fontSize: 14,
                lineHeight: 1.8,
                color: COLORS.grey,
              }}
            >
              Manage how Hindustan Wardrobe keeps you informed about your
              bookings, wardrobe activity and account updates.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "250px minmax(0, 1fr)",
              },
              gap: { xs: 3, md: 5 },
              alignItems: "start",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.white,
                borderRadius: 0,
                overflow: "hidden",
                position: { md: "sticky" },
                top: { md: 100 },
              }}
            >
              <Box
                sx={{
                  backgroundColor: COLORS.black,
                  color: COLORS.white,
                  p: 2.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: COLORS.goldLight,
                    mb: 0.7,
                  }}
                >
                  Account
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: 24,
                  }}
                >
                  My Wardrobe
                </Typography>
              </Box>

              <List sx={{ p: 1.2 }}>
                <SidebarItem
                  icon={<PersonIcon />}
                  label="Profile"
                  onClick={() => navigate("/profile")}
                />

                <SidebarItem
                  icon={<BagIcon />}
                  label="Booking Request"
                  onClick={openAppRedirect}
                />

                <SidebarItem
                  icon={<OrdersIcon />}
                  label="My Orders"
                  onClick={() => navigate("/orders")}
                />

                <SidebarItem
                  icon={<WishlistIcon />}
                  label="Wishlist"
                  onClick={() => navigate("/wishlist")}
                />

                <SidebarItem
                  icon={<SupportAgentIcon />}
                  label="Help & Support"
                  onClick={() => navigate("/contact-us")}
                />

                <SidebarItem
                  icon={<NotificationsNoneRoundedIcon />}
                  label="Settings"
                  active
                  onClick={() => navigate("/settings")}
                />
              </List>
            </Paper>

            <Box>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  borderRadius: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: { xs: 2.5, sm: 4 },
                    py: 3,
                    backgroundColor: COLORS.softCream,
                    borderBottom: `1px solid ${COLORS.border}`,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <NotificationsNoneRoundedIcon
                      sx={{
                        color: COLORS.gold,
                        fontSize: 22,
                      }}
                    />

                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 24,
                          color: COLORS.black,
                        }}
                      >
                        Notification Settings
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.5,
                          fontSize: 12,
                          color: COLORS.grey,
                        }}
                      >
                        Choose the updates you would like to receive.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box sx={{ px: { xs: 2.5, sm: 4 } }}>
                  <SettingRow
                    icon={<EmailOutlinedIcon />}
                    title="Email Notifications"
                    description="Receive booking and account updates via email."
                    checked={settings.email}
                    onChange={() => toggleSetting("email")}
                  />

                  <SettingRow
                    icon={<SmsOutlinedIcon />}
                    title="SMS Notifications"
                    description="Receive important booking updates via SMS."
                    checked={settings.sms}
                    onChange={() => toggleSetting("sms")}
                  />

                  <SettingRow
                    icon={<NotificationsActiveOutlinedIcon />}
                    title="Push Notifications"
                    description="Stay updated with activity and wardrobe alerts."
                    checked={settings.push}
                    onChange={() => toggleSetting("push")}
                  />

                  <SettingRow
                    icon={<CalendarMonthOutlinedIcon />}
                    title="Weekly Summary"
                    description="Receive a weekly overview of your wardrobe activity."
                    checked={settings.weekly}
                    onChange={() => toggleSetting("weekly")}
                    last
                  />
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.black,
                  borderRadius: 0,
                  p: { xs: 2.5, sm: 4 },
                  color: COLORS.white,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: COLORS.goldLight,
                        mb: 1,
                      }}
                    >
                      Stay Connected
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: { xs: 24, sm: 28 },
                        mb: 1,
                      }}
                    >
                      Never miss a wardrobe moment.
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 12,
                        lineHeight: 1.7,
                        color: "#BDB8B0",
                        maxWidth: 500,
                      }}
                    >
                      Keep the notifications that matter and switch off
                      anything you no longer need.
                    </Typography>
                  </Box>

                  <Button
                    onClick={() => navigate("/contact-us")}
                    endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 12 }} />}
                    sx={{
                      minWidth: 170,
                      minHeight: 48,
                      px: 2.5,
                      borderRadius: 0,
                      backgroundColor: COLORS.goldLight,
                      color: COLORS.black,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      "&:hover": {
                        backgroundColor: COLORS.gold,
                        color: COLORS.white,
                      },
                    }}
                  >
                    Need Help
                  </Button>
                </Stack>
              </Paper>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderTop: `1px solid ${COLORS.border}`,
                }}
              >
                <CheckRoundedIcon
                  sx={{
                    fontSize: 17,
                    color: COLORS.gold,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 11,
                    color: COLORS.grey,
                    textAlign: "left",
                  }}
                >
                  Your preferences are applied instantly on this device.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}